import { useState, useEffect, useMemo } from "react";
import { NetworkStatus } from "@apollo/client";
import { useQuery, useMutation, useApolloClient } from "@apollo/client/react";
import {
  GET_CURRENT_USER,
  GET_USER_ORGANIZATIONS,
  GET_ORGANIZATION,
  GET_ORGANIZATION_MEMBERS_CONNECTION,
  GET_CREDIT_TRANSACTIONS_CONNECTION,
  GET_SUBSCRIPTION,
  GET_BILLING_CAPABILITIES,
  GET_PLAN_CONFIGS,
  GET_CREDITS_WITH_BREAKDOWN,
} from "@/graphql/queries";
import {
  UPDATE_USER_PROFILE,
  INVITE_MEMBER,
  REMOVE_MEMBER,
  RESEND_INVITATION,
  REVOKE_INVITATION,
  UPDATE_TENANT_SETTINGS,
  LEAVE_ORGANIZATION,
} from "@/graphql/mutations";
import { useToast } from "@/hooks/use-toast";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import type {
  CurrentUser,
  Member,
  Organization,
  CreditTransaction,
  PageInfo,
  Subscription,
  BillingCapabilities,
  PlanConfig,
  CreditsBreakdown,
  RoomBranding,
} from "./types";

// Mutation response types
interface InviteMemberResponse {
  inviteMember: {
    success: boolean;
    invitation?: {
      id: string;
      email: string;
      role: string;
    };
    error?: string;
  };
}

interface ConnectionPage<T> {
  nodes?: T[] | null;
  pageInfo?: PageInfo | null;
}

interface CreditTransactionsConnectionResponse {
  creditTransactionsConnection?: ConnectionPage<CreditTransaction> | null;
}

interface OrganizationMembersConnectionResponse {
  organizationMembersConnection?: ConnectionPage<Member> | null;
}

const EMPTY_PAGE_INFO: PageInfo = {
  startCursor: null,
  endCursor: null,
  hasNextPage: false,
  hasPreviousPage: false,
};

const TRANSACTIONS_PAGE_SIZE = 100;
const MEMBERS_PAGE_SIZE = 20;

export function useSettingsData() {
  const { toast } = useToast();
  const apolloClient = useApolloClient();

  // State for profile editing
  const [nameValue, setNameValue] = useState<string>("");
  const [phoneValue, setPhoneValue] = useState<string>("");
  const [originalName, setOriginalName] = useState<string>("");
  const [originalPhone, setOriginalPhone] = useState<string>("");

  // State for org settings
  const [inviteEmail, setInviteEmail] = useState("");
  const [maxEmptyTimeout, setMaxEmptyTimeout] = useState<number | string>(10);
  const [originalMaxEmptyTimeout, setOriginalMaxEmptyTimeout] = useState<
    number | string
  >(10);
  const [noModeratorTimeout, setNoModeratorTimeout] = useState<number | string>(30);
  const [originalNoModeratorTimeout, setOriginalNoModeratorTimeout] = useState<number | string>(30);
  const [billingContactName, setBillingContactName] = useState<string>("");
  const [originalBillingContactName, setOriginalBillingContactName] = useState<string>("");
  const [billingContactEmail, setBillingContactEmail] = useState<string>("");
  const [originalBillingContactEmail, setOriginalBillingContactEmail] = useState<string>("");

  // State for branding
  const [brandName, setBrandName] = useState<string>("");
  const [accentColor, setAccentColor] = useState<string>("");
  const [preJoinBgColor, setPreJoinBgColor] = useState<string>("");
  const [welcomeMessage, setWelcomeMessage] = useState<string>("");
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [originalBranding, setOriginalBranding] = useState<RoomBranding | null>(
    null,
  );

  // State for credits
  const [showTopUp, setShowTopUp] = useState(false);

  // Fetch current user data with cache-first policy
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery<{ me?: CurrentUser | null }>(GET_CURRENT_USER, {
    fetchPolicy: "cache-first",
    pollInterval: 300000,
    errorPolicy: "all",
  });

  // Fetch user organizations
  const {
    data: orgsData,
    refetch: refetchUser,
    error: orgsError,
  } = useQuery<{ me?: { organizations?: Array<{ id: string; name: string; role: string; isPersonal?: boolean }> } | null }>(GET_USER_ORGANIZATIONS, { errorPolicy: "all" });

  // Fetch credits data (with breakdown)
  const {
    data: creditsData,
    loading: creditsLoading,
    refetch: refetchCredits,
    error: creditsError,
  } = useQuery<{ credits?: CreditsBreakdown | null }>(GET_CREDITS_WITH_BREAKDOWN, { errorPolicy: "all" });

  // Fetch credit transactions
  const {
    data: transactionsData,
    loading: transactionsLoading,
    error: transactionsError,
    fetchMore: fetchMoreTransactions,
    refetch: refetchTransactions,
    networkStatus: transactionsNetworkStatus,
  } = useQuery<CreditTransactionsConnectionResponse>(GET_CREDIT_TRANSACTIONS_CONNECTION, {
    variables: { first: TRANSACTIONS_PAGE_SIZE },
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  // Fetch subscription data
  const {
    data: subscriptionData,
    loading: subscriptionLoading,
    error: subscriptionError,
  } = useQuery<{ subscription?: Subscription | null }>(GET_SUBSCRIPTION, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  const { data: billingCapabilitiesData, error: billingCapabilitiesError } = useQuery<{
    billingCapabilities?: BillingCapabilities | null;
  }>(GET_BILLING_CAPABILITIES, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    errorPolicy: "all",
  });

  // Fetch plan configurations
  const { data: planConfigsData, error: planConfigsError } = useQuery<{ planConfigs?: PlanConfig[] | null }>(
    GET_PLAN_CONFIGS,
    { errorPolicy: "all" },
  );

  const currentUser: CurrentUser = userData?.me || {
    id: "",
    name: "",
    email: "",
    phone: null,
    avatarURL: null,
    createdAt: null,
    updatedAt: null,
    lastLoginAt: null,
    onboardingCompleted: false,
  };

  // Credits data
  const creditsBreakdown: CreditsBreakdown | null = creditsData?.credits
    ? {
        balance: creditsData.credits.balance || 0,
        subscriptionCredits: creditsData.credits.subscriptionCredits || 0,
        prepaidCredits: creditsData.credits.prepaidCredits || 0,
        billableCredits: creditsData.credits.billableCredits || 0,
        billableAmountUsd: creditsData.credits.billableAmountUsd || 0,
        overageLockActive: Boolean(creditsData.credits.overageLockActive || false),
        monthlyAllocation: creditsData.credits.monthlyAllocation || 0,
        billingCycleEnd: creditsData.credits.billingCycleEnd || undefined,
        usedThisMonth: creditsData.credits.usedThisMonth || 0,
        totalUsed: creditsData.credits.totalUsed || 0,
      }
    : null;

  const credits = creditsBreakdown?.balance || 0;
  const transactions: CreditTransaction[] =
    transactionsData?.creditTransactionsConnection?.nodes || [];
  const transactionsPageInfo: PageInfo =
    transactionsData?.creditTransactionsConnection?.pageInfo || EMPTY_PAGE_INFO;
  const loadingMoreTransactions = transactionsNetworkStatus === NetworkStatus.fetchMore;

  // Subscription data
  const subscription: Subscription | null = subscriptionData?.subscription || null;
  const billingCapabilities: BillingCapabilities | null = billingCapabilitiesData?.billingCapabilities || null;
  const planConfigs: PlanConfig[] = planConfigsData?.planConfigs || [];

  // Update profile state when user data loads
  useEffect(() => {
    if (currentUser.name !== undefined) {
      setNameValue(currentUser.name || "");
      setOriginalName(currentUser.name || "");
    }
    if (currentUser.phone !== undefined) {
      setPhoneValue(currentUser.phone || "");
      setOriginalPhone(currentUser.phone || "");
    }
  }, [currentUser.name, currentUser.phone]);

  const userOrganizations = useMemo(
    () => orgsData?.me?.organizations || [],
    [orgsData?.me?.organizations],
  );
  // Use shared hook to get current organization consistently across the app
  const { currentOrganizationId, isPersonalOrg } =
    useCurrentOrganization(userOrganizations);
  const organizationId = currentOrganizationId || null;

  // Fetch organization details
  const {
    data: orgData,
    loading: orgLoading,
    refetch: refetchOrg,
  } = useQuery<{ organization?: Organization | null }>(GET_ORGANIZATION, {
    variables: { id: organizationId },
    skip: !organizationId,
    errorPolicy: "all",
  });

  const {
    data: membersData,
    loading: membersLoading,
    error: membersError,
    fetchMore: fetchMoreMembers,
    refetch: refetchMembers,
    networkStatus: membersNetworkStatus,
  } = useQuery<OrganizationMembersConnectionResponse>(GET_ORGANIZATION_MEMBERS_CONNECTION, {
    variables: { organizationId, first: MEMBERS_PAGE_SIZE },
    skip: !organizationId,
    errorPolicy: "all",
    notifyOnNetworkStatusChange: true,
  });

  const queryErrors = [
    userError,
    orgsError,
    creditsError,
    transactionsError,
    membersError,
    subscriptionError,
    billingCapabilitiesError,
    planConfigsError,
  ];

  const organization: Organization = useMemo(
    () => orgData?.organization || { id: organizationId || "", name: "Loading..." },
    [orgData?.organization, organizationId],
  );

  useEffect(() => {
    if (organization?.settings?.max_empty_timeout) {
      setMaxEmptyTimeout(organization.settings.max_empty_timeout);
      setOriginalMaxEmptyTimeout(organization.settings.max_empty_timeout);
    }
    if (organization?.settings?.no_moderator_timeout != null) {
      setNoModeratorTimeout(organization.settings.no_moderator_timeout);
      setOriginalNoModeratorTimeout(organization.settings.no_moderator_timeout);
    }
    setBillingContactName(organization?.settings?.billing_contact_name || "");
    setOriginalBillingContactName(organization?.settings?.billing_contact_name || "");
    setBillingContactEmail(organization?.settings?.billing_contact_email || "");
    setOriginalBillingContactEmail(organization?.settings?.billing_contact_email || "");
    if (organization?.settings?.branding) {
      const b = organization.settings.branding;
      setBrandName(b.brand_name || "");
      setAccentColor(b.accent_color || "");
      setPreJoinBgColor(b.pre_join_bg_color || "");
      setWelcomeMessage(b.welcome_message || "");
      setRedirectUrl(b.redirect_url || "");
      setLogoUrl(b.logo_url || null);
      setOriginalBranding(b);
    }
  }, [organization]);

  const members: Member[] = membersData?.organizationMembersConnection?.nodes || [];
  const membersPageInfo: PageInfo =
    membersData?.organizationMembersConnection?.pageInfo || EMPTY_PAGE_INFO;
  const loadingMoreMembers = membersNetworkStatus === NetworkStatus.fetchMore;
  const membersInitialLoading = (orgLoading || membersLoading) && members.length === 0;

  const mergeUniqueById = <T extends { id: string }>(currentItems: T[], incomingItems: T[]) => {
    const existingIds = new Set(currentItems.map((item) => item.id));
    return [
      ...currentItems,
      ...incomingItems.filter((item) => !existingIds.has(item.id)),
    ];
  };

  const handleLoadMoreTransactions = async () => {
    if (!transactionsPageInfo.hasNextPage || !transactionsPageInfo.endCursor || loadingMoreTransactions) {
      return;
    }

    await fetchMoreTransactions({
      variables: {
        first: TRANSACTIONS_PAGE_SIZE,
        after: transactionsPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.creditTransactionsConnection) {
          return previousResult;
        }

        const previousNodes = previousResult.creditTransactionsConnection?.nodes || [];
        const nextNodes = fetchMoreResult.creditTransactionsConnection.nodes || [];

        return {
          creditTransactionsConnection: {
            ...fetchMoreResult.creditTransactionsConnection,
            nodes: mergeUniqueById(previousNodes, nextNodes),
          },
        };
      },
    });
  };

  const handleLoadMoreMembers = async () => {
    if (!organizationId || !membersPageInfo.hasNextPage || !membersPageInfo.endCursor || loadingMoreMembers) {
      return;
    }

    await fetchMoreMembers({
      variables: {
        organizationId,
        first: MEMBERS_PAGE_SIZE,
        after: membersPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.organizationMembersConnection) {
          return previousResult;
        }

        const previousNodes = previousResult.organizationMembersConnection?.nodes || [];
        const nextNodes = fetchMoreResult.organizationMembersConnection.nodes || [];

        return {
          organizationMembersConnection: {
            ...fetchMoreResult.organizationMembersConnection,
            nodes: mergeUniqueById(previousNodes, nextNodes),
          },
        };
      },
    });
  };

  // Update user profile mutation
  const [updateUserProfile, { loading: updatingProfile }] = useMutation(
    UPDATE_USER_PROFILE,
    {
      onCompleted: (data: { updateUserProfile?: CurrentUser | null }) => {
        toast({
          title: "Profile updated",
          description: "Your profile has been updated successfully.",
        });
        // Update original values to reflect saved state
        if (data.updateUserProfile) {
          setOriginalName(data.updateUserProfile.name || "");
          setOriginalPhone(data.updateUserProfile.phone || "");
          apolloClient.cache.writeQuery({
            query: GET_CURRENT_USER,
            data: {
              me: data.updateUserProfile,
            },
          });
        }
        apolloClient.refetchQueries({ include: [GET_CURRENT_USER] });
      },
      onError: (error) => {
        toast({
          title: "Error",
          description:
            error.message || "Failed to update profile. Please try again.",
          variant: "destructive",
        });
      },
    },
  );

  // Invite member mutation
  const [inviteMember, { loading: invitingMember }] =
    useMutation<InviteMemberResponse>(INVITE_MEMBER, {
      refetchQueries: organizationId
        ? [
            {
              query: GET_ORGANIZATION_MEMBERS_CONNECTION,
              variables: { organizationId, first: MEMBERS_PAGE_SIZE },
            },
          ]
        : [],
      onCompleted: () => {
        toast({
          title: "Member invited",
          description: `Invitation email has been sent to ${inviteEmail}.`,
        });
        setInviteEmail("");
        void refetchMembers();
        refetchUser();
      },
      onError: (error) => {
        toast({
          title: "Error",
          description:
            error.message || "Failed to invite member. Please try again.",
          variant: "destructive",
        });
      },
    });

  // Remove member mutation
  const [removeMember] = useMutation(REMOVE_MEMBER, {
    refetchQueries: organizationId
      ? [
          {
            query: GET_ORGANIZATION_MEMBERS_CONNECTION,
            variables: { organizationId, first: MEMBERS_PAGE_SIZE },
          },
        ]
      : [],
    onCompleted: () => {
      void refetchMembers();
      refetchUser();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to remove member. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Resend invitation mutation
  const [resendInvitation] = useMutation(RESEND_INVITATION, {
    refetchQueries: organizationId
      ? [
          {
            query: GET_ORGANIZATION_MEMBERS_CONNECTION,
            variables: { organizationId, first: MEMBERS_PAGE_SIZE },
          },
        ]
      : [],
    onCompleted: () => {
      toast({
        title: "Invitation resent",
        description: "Invitation has been resent successfully.",
      });
      void refetchMembers();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to resend invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Revoke invitation mutation
  const [revokeInvitation] = useMutation(REVOKE_INVITATION, {
    refetchQueries: organizationId
      ? [
          {
            query: GET_ORGANIZATION_MEMBERS_CONNECTION,
            variables: { organizationId, first: MEMBERS_PAGE_SIZE },
          },
        ]
      : [],
    onCompleted: () => {
      toast({
        title: "Invitation revoked",
        description: "The invitation has been cancelled.",
      });
      void refetchMembers();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to revoke invitation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Leave organization mutation
  const [leaveOrganization] = useMutation(LEAVE_ORGANIZATION, {
    onCompleted: async (data: { leaveOrganization?: { success: boolean; error?: string | null } | null }) => {
      if (data?.leaveOrganization?.success) {
        toast({
          title: "Left organization",
          description: "You have successfully left the organization.",
        });
        // Redirect to home or switch to personal org
        await apolloClient.resetStore();
        window.location.href = "/";
      } else {
        toast({
          title: "Error",
          description:
            data?.leaveOrganization?.error || "Failed to leave organization.",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Error",
        description:
          error.message || "Failed to leave organization. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Update settings mutation
  const [updateSettings, { loading: updatingSettings }] = useMutation(
    UPDATE_TENANT_SETTINGS,
    {
      onCompleted: () => {
        toast({
          title: "Settings updated",
          description: "Organization settings have been saved.",
        });
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message || "Failed to update settings.",
          variant: "destructive",
        });
      },
    },
  );

  // Computed values for tracking changes
  const hasProfileChanges =
    nameValue.trim() !== originalName.trim() ||
    phoneValue.trim() !== originalPhone.trim();

  const hasRoomSettingsChanges =
    String(maxEmptyTimeout) !== String(originalMaxEmptyTimeout) ||
    String(noModeratorTimeout) !== String(originalNoModeratorTimeout) ||
    brandName !== (originalBranding?.brand_name || "") ||
    accentColor !== (originalBranding?.accent_color || "") ||
    preJoinBgColor !== (originalBranding?.pre_join_bg_color || "") ||
    welcomeMessage !== (originalBranding?.welcome_message || "") ||
    redirectUrl !== (originalBranding?.redirect_url || "");

  const hasBillingContactChanges =
    billingContactName.trim() !== originalBillingContactName.trim() ||
    billingContactEmail.trim() !== originalBillingContactEmail.trim();

  // Handlers
  const handleUpdateProfile = async () => {
    const trimmedName = nameValue.trim();
    const trimmedPhone = phoneValue.trim();
    await updateUserProfile({
      variables: {
        input: {
          name: trimmedName || null,
          phone: trimmedPhone || null,
        },
      },
    });
  };

  const handleSaveSettings = async () => {
    const timeoutVal =
      typeof maxEmptyTimeout === "string"
        ? parseInt(maxEmptyTimeout)
        : maxEmptyTimeout;
    let finalTimeout = isNaN(timeoutVal) || timeoutVal < 1 ? 1 : timeoutVal;

    if (finalTimeout > 30) {
      finalTimeout = 30;
    }

    if (finalTimeout !== timeoutVal) {
      setMaxEmptyTimeout(finalTimeout);
    }

    const noModVal =
      typeof noModeratorTimeout === "string"
        ? parseInt(noModeratorTimeout)
        : noModeratorTimeout;
    let finalNoModTimeout = isNaN(noModVal) || noModVal < 1 ? 1 : noModVal;
    if (finalNoModTimeout > 120) {
      finalNoModTimeout = 120;
    }
    if (finalNoModTimeout !== noModVal) {
      setNoModeratorTimeout(finalNoModTimeout);
    }

    await updateSettings({
      variables: {
        organizationId,
        settings: {
          max_empty_timeout: finalTimeout,
          no_moderator_timeout: finalNoModTimeout,
          branding: {
            brand_name: brandName,
            accent_color: accentColor,
            pre_join_bg_color: preJoinBgColor,
            welcome_message: welcomeMessage,
            redirect_url: redirectUrl,
          },
        },
      },
    });

    // Update original values after successful save
    setOriginalMaxEmptyTimeout(finalTimeout);
    setOriginalNoModeratorTimeout(finalNoModTimeout);
    setOriginalBranding({
      logo_url: logoUrl,
      brand_name: brandName || null,
      accent_color: accentColor || null,
      pre_join_bg_color: preJoinBgColor || null,
      welcome_message: welcomeMessage || null,
      redirect_url: redirectUrl || null,
    });
  };

  const handleSaveBillingContact = async () => {
    const trimmedBillingContactName = billingContactName.trim();
    const trimmedBillingContactEmail = billingContactEmail.trim();

    await updateSettings({
      variables: {
        organizationId,
        settings: {
          billing_contact_name: trimmedBillingContactName || "",
          billing_contact_email: trimmedBillingContactEmail || "",
        },
      },
    });

    setOriginalBillingContactName(trimmedBillingContactName);
    setOriginalBillingContactEmail(trimmedBillingContactEmail);
    setBillingContactName(trimmedBillingContactName);
    setBillingContactEmail(trimmedBillingContactEmail);
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast({
        title: "Email required",
        description: "Please enter an email address to invite.",
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await inviteMember({
        variables: {
          organizationId,
          email: inviteEmail.trim(),
          role: "MEMBER",
        },
      });

      if (result.data?.inviteMember?.error) {
        toast({
          title: "Could not send invitation",
          description: result.data.inviteMember.error,
          variant: "destructive",
        });
      }
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const handleKickMember = async (memberId: string, memberName: string) => {
    try {
      await removeMember({
        variables: {
          organizationId,
          memberId,
        },
      });
      toast({
        title: "Member removed",
        description: `${memberName} has been removed from the organization.`,
        variant: "destructive",
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation({
        variables: {
          invitationId,
        },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await revokeInvitation({
        variables: {
          invitationId,
        },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const handleLeaveOrganization = async (orgIdToLeave?: string) => {
    const targetOrgId = orgIdToLeave || organizationId;
    if (!targetOrgId) return;

    try {
      await leaveOrganization({
        variables: {
          organizationId: targetOrgId,
        },
      });
    } catch (error) {
      // Error handled by onError callback
    }
  };

  const handleLogoUpload = async (file: File) => {
    if (!organizationId) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);

      const graphqlEndpoint = import.meta.env.VITE_FLOW_GRAPHQL_ENDPOINT || "";
      const baseUrl = graphqlEndpoint.replace(/\/graphql(\/.*)?$/, "");
      const res = await fetch(`${baseUrl}/upload/branding-logo`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Upload failed");
      }

      const data = await res.json();
      setLogoUrl(data.url);
      toast({
        title: "Logo uploaded",
        description: "Your branding logo has been updated.",
      });
      refetchOrg();
    } catch (error) {
      toast({
        title: "Upload failed",
        description: (error as Error).message || "Failed to upload logo.",
        variant: "destructive",
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoRemove = async () => {
    if (!organizationId) return;
    try {
      const graphqlEndpoint = import.meta.env.VITE_FLOW_GRAPHQL_ENDPOINT || "";
      const baseUrl = graphqlEndpoint.replace(/\/graphql(\/.*)?$/, "");
      const res = await fetch(`${baseUrl}/upload/branding-logo`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "Failed to remove logo");
      }

      setLogoUrl(null);
      toast({
        title: "Logo removed",
        description: "Your branding logo has been removed.",
      });
      refetchOrg();
    } catch (error) {
      toast({
        title: "Error",
        description: (error as Error).message || "Failed to remove logo.",
        variant: "destructive",
      });
    }
  };

  const handleTopUpComplete = () => {
    refetchCredits();
    void refetchTransactions();
    setShowTopUp(false);
  };

  return {
    // Query errors for error handling
    queryErrors,

    // User data
    currentUser,
    userLoading,

    // Profile editing
    nameValue,
    setNameValue,
    phoneValue,
    setPhoneValue,
    updatingProfile,
    hasProfileChanges,
    handleUpdateProfile,

    // Organization data
    organization,
    organizationId,
    members,
    isPersonalOrg,
    userOrganizations,
    orgLoading: membersInitialLoading,
    membersPageInfo,
    loadingMoreMembers,
    handleLoadMoreMembers,

    // Member management
    inviteEmail,
    setInviteEmail,
    invitingMember,
    handleInviteMember,
    handleKickMember,
    handleResendInvitation,
    handleRevokeInvitation,
    handleLeaveOrganization,

    // Room settings
    maxEmptyTimeout,
    setMaxEmptyTimeout,
    noModeratorTimeout,
    setNoModeratorTimeout,
    billingContactName,
    setBillingContactName,
    billingContactEmail,
    setBillingContactEmail,
    updatingSettings,
    hasRoomSettingsChanges,
    hasBillingContactChanges,
    handleSaveSettings,
    handleSaveBillingContact,

    // Branding
    brandName,
    setBrandName,
    accentColor,
    setAccentColor,
    preJoinBgColor,
    setPreJoinBgColor,
    welcomeMessage,
    setWelcomeMessage,
    redirectUrl,
    setRedirectUrl,
    logoUrl,
    uploadingLogo,
    handleLogoUpload,
    handleLogoRemove,

    // Credits
    credits,
    creditsLoading,
    transactions,
    transactionsLoading,
    transactionsPageInfo,
    loadingMoreTransactions,
    handleLoadMoreTransactions,
    showTopUp,
    setShowTopUp,
    handleTopUpComplete,

    // Subscription
    subscription,
    subscriptionLoading,
    billingCapabilities,
    planConfigs,
    creditsBreakdown,
  };
}
